import React, { useEffect, useState } from 'react';

import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import { useScope } from '../../context/PermissionContext';
import useDepartmentStore from '../../stores/departmentStore';
import { STAFF_ROLES } from '../../constants/staff';
import {
  ADMISSION_ACCESS_ERROR_CODE,
  ADMISSION_ACCESS_ERROR_COPY,
  CARE_TEAM_DEFAULT_LIMIT,
  careTeamLimitCopy,
  doctorDisplayName,
  CARE_TEAM_UNNAMED_DOCTOR,
  TREATING_DOCTORS_HINT_COPY,
  NO_ADDED_DOCTORS_COPY,
  ADD_DOCTOR_HELPER_COPY,
  NO_PRIMARY_DOCTOR_COPY,
  CHANGE_DOCTOR_LABEL,
  REMOVE_DOCTOR_KEEP_LABEL,
  REMOVE_DOCTOR_CTA_LABEL,
  removeDoctorTitle,
  REMOVE_YOURSELF_TITLE,
  REMOVE_DOCTOR_BODY,
  REMOVE_YOURSELF_BODY,
  REMOVE_DOCTOR_FAILED_COPY,
  isDeathOutcome,
  isTerminal,
} from '../../constants/admission';
import { removeTreatingDoctor } from '../../services/api/admissionApi';
import AddTreatingDoctorDialog, {
  CARE_TEAM_RELOAD_CODES, careTeamErrorCopy,
} from './AddTreatingDoctorDialog';

/**
 * TreatingDoctorsCard — "Treating doctors" on the admission Details
 * (admission-access C-6, mockup 5-treating-doctors): the primary doctor (with
 * Change) and the added doctors as chips. Reads everything off the admission
 * row (`added_doctors`, `viewer_relationship`) — it fetches nothing itself, and
 * after a write the caller's `onChanged` reloads the admission so the row stays
 * the single source.
 *
 * Controls (C-7): visible iff ADMIT_PATIENT OR the viewer is the primary / an
 * added doctor (`viewer_relationship != null`) OR the viewer is the duty doctor
 * (a DOCTOR with no active department, in an organisation that HAS departments;
 * mirrors the server's can_manage_care_team), and the care team is not
 * locked. Everyone else (nurse, other doctors, billing) sees the list read-only.
 * The primary can only be CHANGED (existing Change doctor dialog), never removed.
 */

// Chips shown before "+n more" (long-list rule).
const CHIPS_SHOWN = 4;

// Copy: ratified (#35, #37, #38, #47, #50-#52, #54) — constants/admission.js.
// Re-exported for the existing imports.
export { TREATING_DOCTORS_HINT_COPY, NO_ADDED_DOCTORS_COPY } from '../../constants/admission';

/** Same lock rule as the Discharge Summary (every terminal status but DECEASED). */
export const isCareTeamLocked = (status) => isTerminal(status) && !isDeathOutcome(status);

/**
 * Render-prop: `children(true)` when the viewer is a DOCTOR who is neither the
 * primary nor an added doctor of `admission` (the summary Save and the discharge
 * steps are theirs to be refused: 403 NOT_TREATING_DOCTOR), else `children(false)`.
 * `viewer_relationship` is `null` for such a doctor AND for every non-doctor
 * (receptionist, admin, billing, nurse), so the role decides — read (`useScope`)
 * only for a row whose relationship is explicitly `null`. A relationship, or the
 * field absent on an older server, is never gated (today's behaviour) and reads
 * nothing from PermissionContext.
 */
function DoctorRoleGate({ children }) {
  const { role } = useScope();
  return children(role === STAFF_ROLES.DOCTOR);
}
export function NonTreatingDoctorGate({ admission, children }) {
  if (!admission || admission.viewer_relationship !== null) return children(false);
  return <DoctorRoleGate>{children}</DoctorRoleGate>;
}

/**
 * The duty doctor / RMO (server RC-1): a DOCTOR with no department of their own
 * (`viewerDeptIds` loaded and empty) in an organisation that HAS active
 * departments. With no departments in the org there is no duty doctor, so a
 * non-treating doctor gets no control. Fetches the department list only for a
 * DOCTOR viewer, and only when it is missing (store guards a repeat).
 */
export function useIsDutyDoctor() {
  const { role, viewerDeptIds } = useScope();
  const departments = useDepartmentStore((s) => s.departments);
  const deptsLoaded = useDepartmentStore((s) => s.deptsLoaded);
  const deptsLoading = useDepartmentStore((s) => s.isLoading);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const isDoctor = role === STAFF_ROLES.DOCTOR;
  useEffect(() => {
    if (isDoctor && !deptsLoaded && !deptsLoading) fetchDepartments();
  }, [isDoctor, deptsLoaded, deptsLoading, fetchDepartments]);
  return isDoctor
    && Array.isArray(viewerDeptIds) && viewerDeptIds.length === 0
    && (departments || []).some((d) => d.active !== false);
}

const label = 'text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5';
const linkBtn = 'text-[12px] font-semibold text-teal-700 dark:text-teal-300 underline';

function DoctorChip({ doctor, isYou, onRemove }) {
  const name = doctorDisplayName(doctor.name) || CARE_TEAM_UNNAMED_DOCTOR;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-100 text-[12.5px] pl-2.5 pr-2 py-1"
      data-testid={`treating-doctor-chip-${doctor.member_id}`}
    >
      <span>{name}{isYou ? ' (you)' : ''}</span>
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(doctor)}
          className="text-slate-500 dark:text-slate-300 hover:text-red-700 dark:hover:text-red-300 leading-none"
          aria-label={`Remove ${name}`}
          data-testid={`treating-doctor-remove-${doctor.member_id}`}
        >
          ✕
        </button>
      )}
    </span>
  );
}

export default function TreatingDoctorsCard({ admission, canAdmitPatient, onChangePrimary, onChanged }) {
  const { memberId, role } = useScope();
  const a = admission;
  const added = a.added_doctors || [];
  const locked = isCareTeamLocked(a.status);
  const isDutyDoctor = useIsDutyDoctor();
  const canManage = !!canAdmitPatient || a.viewer_relationship != null || isDutyDoctor;
  const canRemove = canManage && !locked;
  // `onChangePrimary` is the workspace's ADMIT_PATIENT-gated Change doctor
  // opener (undefined without the slug); the taskbar is closed once terminal.
  const canChange = typeof onChangePrimary === 'function' && !isTerminal(a.status);
  const addBlockedReason = locked
    ? ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.ADMISSION_TERMINAL]
    // Added-doctor cap: COPY-FE's `CARE_TEAM_DEFAULT_LIMIT` (= BE CARE_TEAM_MAX;
    // the primary is not counted) and the SAME ratified sentence as the 409 (#3).
    : (added.length >= CARE_TEAM_DEFAULT_LIMIT ? careTeamLimitCopy(CARE_TEAM_DEFAULT_LIMIT) : null);

  const [addOpen, setAddOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('');
  const [removing, setRemoving] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const total = (a.primary_doctor_member_id ? 1 : 0) + added.length;
  const needle = filter.trim().toLowerCase();
  const listed = expanded
    ? (needle ? added.filter((d) => (doctorDisplayName(d.name) || '').toLowerCase().includes(needle)) : added)
    : added.slice(0, CHIPS_SHOWN);
  const onRemove = canRemove ? (d) => { setRemoveError(null); setRemoving(d); } : undefined;
  const chip = (d) => <DoctorChip key={d.member_id} doctor={d} isYou={d.member_id === memberId} onRemove={onRemove} />;

  const closeRemove = () => { if (!removeBusy) setRemoving(null); };
  const runRemove = async () => {
    if (removeBusy || !removing) return;
    setRemoveBusy(true);
    setRemoveError(null);
    const res = await removeTreatingDoctor(a.admission_id, removing.member_id);
    // Reload on success, and (before the message) when the failure says our
    // view of the admission was stale.
    if ((res.success || CARE_TEAM_RELOAD_CODES.has(res.code)) && typeof onChanged === 'function') await onChanged();
    setRemoveBusy(false);
    if (res.success) setRemoving(null);
    else setRemoveError(careTeamErrorCopy(res, REMOVE_DOCTOR_FAILED_COPY, role));
  };
  const removingSelf = !!removing && removing.member_id === memberId;

  return (
    <div className="mb-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4" data-testid="treating-doctors-card">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="text-[14px] font-bold text-slate-900 dark:text-white">Treating doctors</h4>
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200" data-testid="treating-doctors-total">
          {total} {total === 1 ? 'doctor' : 'doctors'}
        </span>
        <span className="flex-1" />
        {canManage && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            disabled={!!addBlockedReason}
            title={addBlockedReason || undefined}
            className="px-2.5 py-1.5 text-[12.5px] rounded-md border border-teal-400 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 font-semibold hover:bg-teal-100 dark:hover:bg-teal-900/50 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="treating-doctors-add"
          >
            + Add doctor
          </button>
        )}
      </div>
      {canManage && addBlockedReason && (
        <div className="mt-1.5 text-[11.5px] text-slate-500 dark:text-slate-400" data-testid="treating-doctors-add-reason">{addBlockedReason}</div>
      )}

      <div className="mt-3">
        <div className={label}>Primary doctor</div>
        {a.primary_doctor_member_id ? (
          <div className="flex items-center gap-2.5" data-testid="treating-doctors-primary">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 dark:text-white">
                {doctorDisplayName(a.primary_doctor_name) || CARE_TEAM_UNNAMED_DOCTOR}{a.primary_doctor_member_id === memberId ? ' (you)' : ''}
              </div>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400">{a.department_name || 'No department'} · can see the bill</div>
            </div>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200">Primary</span>
            {canChange && (
              <button type="button" className={linkBtn} onClick={onChangePrimary} data-testid="treating-doctors-change">{CHANGE_DOCTOR_LABEL}</button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 text-[12.5px] p-2.5" data-testid="treating-doctors-no-primary">
            <span className="flex-1">{NO_PRIMARY_DOCTOR_COPY}</span>
            {canChange && (
              <button type="button" className="px-2.5 py-1.5 text-[12.5px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100" onClick={onChangePrimary} data-testid="treating-doctors-assign">Assign doctor</button>
            )}
          </div>
        )}
      </div>

      <div className="mt-3">
        <div className={label}>Added doctors{added.length ? ` · ${added.length}` : ''}</div>
        {added.length === 0 ? (
          <div className="text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="treating-doctors-empty">
            <b className="text-slate-700 dark:text-slate-200">{NO_ADDED_DOCTORS_COPY}</b>{' '}{ADD_DOCTOR_HELPER_COPY}
          </div>
        ) : expanded ? (
          <div data-testid="treating-doctors-expanded">
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
                placeholder="Find an added doctor…"
                aria-label="Find an added doctor"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="treating-doctors-find"
              />
              <button type="button" className={linkBtn} onClick={() => { setExpanded(false); setFilter(''); }} data-testid="treating-doctors-less">Show fewer</button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-[160px] overflow-y-auto">
              {listed.length > 0
                ? listed.map(chip)
                : <span className="text-[12.5px] text-slate-500 dark:text-slate-400">No added doctor matches “{filter.trim()}”.</span>}
            </div>
            <div className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500">Showing {listed.length} of {added.length}</div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="treating-doctors-chips">
            {listed.map(chip)}
            {added.length > CHIPS_SHOWN && (
              <button
                type="button"
                className="text-[12px] font-semibold px-2.5 py-1 rounded-full border border-dashed border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-200"
                onClick={() => setExpanded(true)}
                data-testid="treating-doctors-more"
              >
                +{added.length - CHIPS_SHOWN} more
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-start gap-1.5 text-[11.5px] text-slate-500 dark:text-slate-400" data-testid="treating-doctors-hint">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-none mt-0.5">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span>{TREATING_DOCTORS_HINT_COPY}</span>
      </div>

      {addOpen && (
        <AddTreatingDoctorDialog admission={a} onClose={() => setAddOpen(false)} onChanged={onChanged} />
      )}
      {removing && (
        <ConfirmationModal
          isOpen
          variant="danger"
          icon="⚠️"
          autoFocusCancel
          title={removingSelf ? REMOVE_YOURSELF_TITLE : removeDoctorTitle(doctorDisplayName(removing.name) || CARE_TEAM_UNNAMED_DOCTOR)}
          message={removingSelf ? REMOVE_YOURSELF_BODY : REMOVE_DOCTOR_BODY}
          ctaLabel={removeBusy ? 'Removing…' : REMOVE_DOCTOR_CTA_LABEL}
          cancelLabel={REMOVE_DOCTOR_KEEP_LABEL}
          ctaDisabled={removeBusy}
          onConfirm={runRemove}
          onCancel={closeRemove}
        >
          {removeError && (
            <div role="alert" className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5 text-left" data-testid="treating-doctors-remove-error">
              {removeError}
            </div>
          )}
        </ConfirmationModal>
      )}
    </div>
  );
}
